import React from 'react';
import { Container, Grid, Text, List, Link } from 'basis';
import { LayoutContent } from 'components';
import { PRODUCT_CODE_LOW_RATE, PRODUCT_DAVID_JONES } from '_config';
import { useDataLayerAndAnalyticsLinks } from 'hooks';

import { useTheme } from '@emotion/react';
import { byCountry, byCard, canGoBackToMerchant, getProductName } from 'utils';
import { ExternalLinkIcon } from '../../../components/icons';

export function OutcomeReferred({ storeState }) {
  const theme = useTheme();

  const RegionResponse = byCountry({
    AU: AuOutcomeReferred,
    NZ: NzV2OutcomeReferred,
  });

  return <RegionResponse storeState={storeState} theme={theme} />;
}

const AuOutcomeReferred = ({ storeState, theme }) => {
  const { applicationRef, applyAndBuy } = storeState;
  const productName = getProductName();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);

  return (
    <Container testId="AU-outcome-referred" margin="0 0 0 0" hasBreakpointWidth>
      <LayoutContent rowsGap="0">
        <Container padding="0" margin="0">
          <Grid preset="page" colsGap={6}>
            <Grid.Item colSpan="all">
              <Grid rowsGap={6}>
                <Container padding="8 0 0 0">
                  <Text as="h1" textStyle="heading5" textStyle-md="heading4" color={theme.headingColor}>
                    We have received your application!
                  </Text>
                </Container>
                <Text>
                  Application reference number: <strong data-hj-suppress>{`${applicationRef}`}</strong>
                </Text>
                <Text>
                  Thank you for applying for a {productName}{' '}
                  {byCard({
                    [PRODUCT_CODE_LOW_RATE]: null,
                    default: 'credit card',
                  })}
                  .
                </Text>
                <Text>
                  Right now, your application is being reviewed by one of our team members.
                  <div>We'll get back to you as soon as possible to confirm next steps.</div>
                </Text>

                {canGoBackToMerchant(storeState) && (
                  <>
                    <Text>To continue shopping online at {applyAndBuy?.merchantGroupName}, you can:</Text>
                    <List type="ordered">
                      <List.Item>Shop using another form of payment; or</List.Item>
                      <List.Item>
                        Save your items to your wish list. Once you're approved, you can complete your purchase.
                      </List.Item>
                    </List>
                    <Container>
                      <Link
                        href={applyAndBuy.upstreamUrl}
                        appearance="primary-button"
                        testId="return-to-merchant-btn"
                        margin="6 0"
                        onClick={() => {
                          trackEventSender({
                            location: 'outcome',
                            label: `Return to ${applyAndBuy?.merchantGroupName}`,
                            action: 'external-link',
                          });
                        }}
                        newTab
                      >
                        Return to {applyAndBuy?.merchantGroupName} <ExternalLinkIcon style={{ marginLeft: '8px' }} />
                      </Link>
                    </Container>
                  </>
                )}

                <Text>Thank you for your patience.</Text>
                <Text>
                  The {productName}{' '}
                  {byCard({
                    [PRODUCT_DAVID_JONES]: 'credit card team',
                    default: 'Team',
                  })}
                </Text>
              </Grid>
            </Grid.Item>
          </Grid>
        </Container>
      </LayoutContent>
    </Container>
  );
};

const NzV2OutcomeReferred = ({ storeState, theme }) => {
  const { applicationRef, applyAndBuy } = storeState;
  const productName = getProductName();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);

  return (
    <Container testId="NZ-outcome-referred" margin="0 0 0 0" hasBreakpointWidth>
      <LayoutContent rowsGap="0">
        <Container padding="0" margin="0">
          <Grid preset="page" colsGap={6}>
            <Grid.Item colSpan="all">
              <Grid rowsGap={6}>
                <Container padding="8 0 0 0">
                  <Text as="h1" textStyle="heading5" textStyle-md="heading4" color={theme.headingColor}>
                    We’ve successfully received your application
                  </Text>
                </Container>
                <Text>
                  Application reference number: <strong data-hj-suppress>{`${applicationRef}`}</strong>
                </Text>
                <Text>Thank you for applying for a {productName} credit card.</Text>
                <Text>
                  Right now, your application is being reviewed by one of our team members.
                  <div>We'll get back to you as soon as possible to confirm next steps.</div>
                </Text>

                {canGoBackToMerchant(storeState) && (
                  <>
                    <Text>To continue shopping online at {applyAndBuy?.merchantGroupName}, you can:</Text>
                    <List type="ordered">
                      <List.Item>Shop using another form of payment; or</List.Item>
                      <List.Item>
                        Save your items to your wish list. Once you're approved, you can complete your purchase.
                      </List.Item>
                    </List>
                    <Container>
                      <Link
                        href={applyAndBuy.upstreamUrl}
                        appearance="primary-button"
                        testId="return-to-merchant-btn"
                        margin="6 0"
                        onClick={() => {
                          trackEventSender({
                            location: 'outcome',
                            label: `Return to ${applyAndBuy?.merchantGroupName}`,
                            action: 'external-link',
                          });
                        }}
                        newTab
                      >
                        Return to {applyAndBuy?.merchantGroupName} <ExternalLinkIcon style={{ marginLeft: '8px' }} />
                      </Link>
                    </Container>
                  </>
                )}

                <Text>Thank you for your patience.</Text>
                <Text>The {productName} Team</Text>
              </Grid>
            </Grid.Item>
          </Grid>
        </Container>
      </LayoutContent>
    </Container>
  );
};
